import { useCallback, useMemo } from 'react'

import { ResolveBlobUrlDocument, ResolveBlobUrlMutation } from 'generated/resources/graphql'

import { UploadStatus, useDirectUpload } from 'modules/core/hooks/useDirectUpload'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'

export type ImageUploadResult = {
  url: string
  signedId: string
}

export const useImageUploadForEditor = (
  config: ImageUploadForEditorConfig
): UseImageUploadForEditorReturn => {
  const { upload, progress, status } = useDirectUpload({
    resourceType: config.resourceType,
    resourceId: config.resourceId,
  })

  const [resolveBlobUrl] = useMutation(ResolveBlobUrlDocument, {
    extractorFn: (data: ResolveBlobUrlMutation) =>
      data.directUploadsMutations.getBlobUrlDirectuploadsDirectUpload,
  })

  const onUpload = useCallback(
    async (file: File): Promise<ImageUploadResult> => {
      const result = await upload(file)
      if (result.type === 'aborted') {
        throw new Error('Upload cancelled')
      }

      const handled = handleResult(
        await resolveBlobUrl({
          variables: { signedId: result.signedId },
        }),
        () => {
          // Form errors are displayed via flash by handleResult
        }
      )

      if (!handled.success || !handled.data.directUpload?.url) {
        throw new Error('Failed to get blob URL')
      }

      return {
        url: handled.data.directUpload.url,
        signedId: result.signedId,
      }
    },
    [upload, resolveBlobUrl]
  )

  return useMemo(
    () => ({
      onUpload,
      progress,
      status,
    }),
    [onUpload, progress, status]
  )
}

interface ImageUploadForEditorConfig {
  resourceType: string
  resourceId?: string
}

interface UseImageUploadForEditorReturn {
  onUpload: (file: File) => Promise<ImageUploadResult>
  progress: number
  status: UploadStatus
}
